import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraIconButton, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Check, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useDecideTeamMemory, useDeleteMemory, usePendingTeamMemories, useProposeTeamMemory, useTeamMemories } from '../../hooks/useMemories';
import { apiErrorMessage } from '../../lib/api/apiErrorMessage';
/**
 * Team notes apply to everyone's assistant. Anyone can propose one; an admin
 * approves it first (an admin's own note applies at once). Admins see the
 * proposals waiting for them here, and are notified when one arrives.
 */ export function TeamTab() {
    const active = useTeamMemories();
    const pending = usePendingTeamMemories();
    const isAdmin = pending.isAdmin;
    const propose = useProposeTeamMemory();
    const del = useDeleteMemory();
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const submit = async ()=>{
        setError('');
        setMessage('');
        try {
            const resp = await propose.mutateAsync({
                title: title.trim(),
                body: body.trim()
            });
            setTitle('');
            setBody('');
            setMessage(resp.memory?.status === 'pending' ? 'Sent to your admins. It applies once one of them approves it.' : 'Added. It applies to everyone now.');
        } catch (err) {
            setError(apiErrorMessage(err, 'Could not save the note.'));
        }
    };
    const waiting = pending.data ?? [];
    const notes = active.data ?? [];
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex-1 overflow-y-auto p-5",
                "data-testid": Testids.memory.teamList,
                children: [
                    isAdmin && waiting.length > 0 && /*#__PURE__*/ _jsxs("section", {
                        className: "mb-5",
                        "data-testid": Testids.memory.pendingList,
                        children: [
                            /*#__PURE__*/ _jsxs("h3", {
                                className: "mb-2 text-sm font-semibold text-foreground",
                                children: [
                                    "Waiting for your approval (",
                                    waiting.length,
                                    ")"
                                ]
                            }),
                            waiting.map((row)=>/*#__PURE__*/ _jsx(PendingRow, {
                                    row: row
                                }, row.id))
                        ]
                    }),
                    /*#__PURE__*/ _jsx("h3", {
                        className: "mb-2 text-sm font-semibold text-foreground",
                        children: "For everyone"
                    }),
                    active.isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "Loading…"
                    }),
                    !active.isLoading && notes.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No team notes yet. Add things everyone's assistant should know, like your fiscal year, preferred terms or how you write bios."
                    }),
                    notes.map((row)=>/*#__PURE__*/ _jsx("div", {
                            className: "mb-3 rounded-lg border border-border p-3",
                            "data-testid": Testids.memory.teamItem,
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-start justify-between gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "font-medium text-foreground",
                                                children: row.title || '(untitled)'
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "mt-1 whitespace-pre-wrap text-sm text-muted-foreground",
                                                children: row.body
                                            })
                                        ]
                                    }),
                                    isAdmin && /*#__PURE__*/ _jsx(SpecraIconButton, {
                                        label: "Remove team note",
                                        size: "sm",
                                        variant: "secondary",
                                        "data-testid": Testids.memory.deleteButton,
                                        onClick: ()=>del.mutate(row),
                                        children: /*#__PURE__*/ _jsx(Trash2, {
                                            className: "h-3.5 w-3.5"
                                        })
                                    })
                                ]
                            })
                        }, row.id))
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "border-t border-border p-5",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "mb-2 text-xs text-muted-foreground",
                        children: isAdmin ? 'You are an admin: your note applies to everyone right away.' : 'Your note goes to an admin to approve before it applies to everyone.'
                    }),
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        className: "mb-2",
                        placeholder: "Title",
                        value: title,
                        onChange: (e)=>setTitle(e.target.value),
                        "data-testid": Testids.memory.titleInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        className: "mb-3",
                        rows: 3,
                        placeholder: "What everyone's assistant should know",
                        value: body,
                        onChange: (e)=>setBody(e.target.value),
                        "data-testid": Testids.memory.bodyInput
                    }),
                    error && /*#__PURE__*/ _jsx("p", {
                        className: "mb-2 text-sm text-destructive",
                        children: error
                    }),
                    message && /*#__PURE__*/ _jsx("p", {
                        className: "mb-2 text-sm text-muted-foreground",
                        children: message
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-end",
                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            onClick: submit,
                            disabled: propose.isPending || !title.trim() && !body.trim(),
                            "data-testid": Testids.memory.saveButton,
                            children: isAdmin ? 'Add for everyone' : 'Propose to team'
                        })
                    })
                ]
            })
        ]
    });
}
function PendingRow({ row }) {
    const decide = useDecideTeamMemory();
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const act = async (approve)=>{
        setError('');
        try {
            await decide.mutateAsync({
                id: row.id,
                approve,
                note: note.trim()
            });
        } catch (err) {
            setError(apiErrorMessage(err, 'Could not save your decision.'));
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "mb-3 rounded-lg border border-dashed border-border p-3",
        "data-testid": Testids.memory.pendingItem,
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "font-medium text-foreground",
                children: row.title || '(untitled)'
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "mt-1 whitespace-pre-wrap text-sm text-muted-foreground",
                children: row.body
            }),
            /*#__PURE__*/ _jsx(SpecraInput, {
                className: "mt-2",
                placeholder: "Note to the person who proposed it (optional)",
                value: note,
                onChange: (e)=>setNote(e.target.value),
                "data-testid": Testids.memory.decisionNote
            }),
            error && /*#__PURE__*/ _jsx("p", {
                className: "mt-1 text-sm text-destructive",
                children: error
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-2 flex justify-end gap-2",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        disabled: decide.isPending,
                        onClick: ()=>act(false),
                        "data-testid": Testids.memory.rejectButton,
                        children: [
                            /*#__PURE__*/ _jsx(X, {
                                className: "mr-1 h-3.5 w-3.5"
                            }),
                            "Reject"
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        size: "sm",
                        disabled: decide.isPending,
                        onClick: ()=>act(true),
                        "data-testid": Testids.memory.approveButton,
                        children: [
                            /*#__PURE__*/ _jsx(Check, {
                                className: "mr-1 h-3.5 w-3.5"
                            }),
                            "Approve"
                        ]
                    })
                ]
            })
        ]
    });
}
